import React, { useState, useEffect } from 'react';
import api from '../api';
import SEO from './SEO';
import { Quote, Star, ArrowRight, X, Image as ImageIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import testimonialsBg from '../assets/testimonials_hero_v2.webp';
import kathakaliBg from '../assets/kathakali_bg.webp';

const TestimonialSkeleton = () => (
  <div className="bg-white p-8 rounded-[3rem] border border-slate-100 shadow-[0_20px_50px_rgba(0,0,0,0.35)] animate-pulse flex flex-col justify-between h-full">
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div className="flex gap-1">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="w-3.5 h-3.5 bg-slate-100 rounded-full"></div>
          ))}
        </div>
        <div className="w-10 h-10 bg-slate-50 rounded-xl"></div>
      </div>
      <div className="space-y-3">
        <div className="h-4 bg-slate-100 rounded-full w-full"></div>
        <div className="h-4 bg-slate-100 rounded-full w-[90%]"></div>
        <div className="h-4 bg-slate-100 rounded-full w-[80%]"></div>
      </div>
    </div>

    <div className="flex items-center gap-4 pt-6 mt-8 border-t border-slate-50">
      <div className="w-11 h-11 rounded-xl bg-slate-100"></div>
      <div className="space-y-2">
        <div className="h-4 bg-slate-100 rounded-full w-24"></div>
        <div className="h-3 bg-slate-50 rounded-full w-16"></div>
      </div>
    </div>
  </div>
);

const TestimonialCard = ({ testimonial, index, onClick }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: index * 0.1 }}
    viewport={{ once: true }}
    onClick={() => onClick(testimonial)}
    className="bg-white p-8 rounded-[3rem] border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all cursor-pointer group flex flex-col justify-between h-full"
  >
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div className="flex gap-1">
          {[...Array(5)].map((_, i) => (
            <Star
              key={i}
              size={14}
              className={i < (testimonial.rating || 5) ? "fill-orange-400 text-orange-400" : "text-slate-200"}
            />
          ))}
        </div>
        <Quote className="text-slate-100 group-hover:text-orange-100 transition-colors" size={40} />
      </div>
      <p className="text-lg text-slate-800 leading-relaxed line-clamp-4 italic">
        "{testimonial.content}"
      </p>
      <div className="pt-2">
        <span className="text-[10px] font-black text-orange-500 uppercase tracking-widest group-hover:underline flex items-center gap-2">
          Read Full Story <ArrowRight size={12} />
        </span>
      </div>
    </div>

    <div className="flex items-center gap-4 pt-6 mt-8 border-t border-slate-50">
      <img
        src={testimonial.avatar_url || `https://i.pravatar.cc/150?u=${testimonial.id}`}
        alt={testimonial.name}
        className="w-11 h-11 rounded-xl object-cover shadow-sm ring-4 ring-slate-50"
      />
      <div>
        <h4 className="text-md font-black text-slate-900 uppercase tracking-tight leading-none">{testimonial.name}</h4>
        <p className="text-orange-500 text-[10px] font-bold uppercase tracking-widest mt-1.5">{testimonial.role || "Verified Traveler"}</p>
      </div>
    </div>
  </motion.div>
);

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    content: '',
    avatar_url: '',
    rating: 5
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchTestimonials();
    const savedUser = localStorage.getItem('user');
    if (savedUser) setUser(JSON.parse(savedUser));
  }, []);

  const fetchTestimonials = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/testimonials');
      setTestimonials(response.data);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'content') {
      const words = value.trim().split(/\s+/);
      if (words.length <= 200 || value.length < formData.content.length) {
        setFormData(prev => ({ ...prev, [name]: value }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const wordCount = formData.content.trim() ? formData.content.trim().split(/\s+/).length : 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await api.post('/testimonials', formData);
      setSubmitStatus('success');
      setFormData({ name: '', role: '', content: '', avatar_url: '', rating: 5 });
      setShowForm(false);
      fetchTestimonials();
      setTimeout(() => setSubmitStatus(null), 3000);
    } catch (error) {
      console.error('Error submitting testimonial:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-[#FAF9F6] min-h-screen relative">
      <SEO 
        title="Client Reviews & Testimonials" 
        description="Read authentic travel reviews and testimonials from our clients who have experienced custom-tailored luxury holidays with Sunshine Travels." 
        canonicalPath="/testimonials" 
      />
      {/* Refined Large Testimonial Modal */}
      <AnimatePresence>
        {selectedTestimonial && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-10 bg-slate-900/90 backdrop-blur-md"
            onClick={() => setSelectedTestimonial(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white w-full max-w-6xl h-full max-h-[85vh] rounded-[3rem] overflow-hidden shadow-2xl flex flex-col md:flex-row relative"
            >
              {/* Close Button */}
              <button
                onClick={() => setSelectedTestimonial(null)}
                className="absolute top-6 right-6 z-50 w-10 h-10 bg-slate-900 text-white rounded-full flex items-center justify-center hover:bg-orange-500 transition-all shadow-lg"
              >
                <X size={20} />
              </button>

              {/* Left Side: Immersive Image */}
              <div className="md:w-5/12 h-56 md:h-full relative bg-slate-900 flex items-center justify-center">
                <img
                  src={selectedTestimonial.avatar_url || `https://i.pravatar.cc/800?u=${selectedTestimonial.id}`}
                  alt={selectedTestimonial.name}
                  className="w-full h-full object-contain"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent md:hidden"></div>
              </div>

              {/* Right Side: Scrollable Content */}
              <div className="md:w-7/12 h-full flex flex-col">
                <div className="p-8 md:p-16 overflow-y-auto custom-scrollbar flex-1">
                  <div className="space-y-8">
                    <div className="space-y-2">
                      <div className="flex gap-1 mb-4">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            size={18}
                            className={i < (selectedTestimonial.rating || 5) ? "fill-orange-400 text-orange-400" : "text-slate-200"}
                          />
                        ))}
                      </div>
                      <h3 className="text-4xl md:text-6xl font-serif font-bold text-slate-900 tracking-wide leading-snug">
                        {selectedTestimonial.name}
                      </h3>
                      <p className="text-orange-500 font-bold uppercase tracking-[0.3em] text-[10px]">
                        {selectedTestimonial.role || "Verified Traveler"}
                      </p>
                    </div>

                    <div className="relative pt-4">
                      <Quote className="absolute -top-4 -left-4 text-slate-50" size={80} />
                      <p className="relative z-10 text-lg md:text-2xl text-slate-700 font-medium leading-relaxed italic">
                        "{selectedTestimonial.content}"
                      </p>
                    </div>

                    <div className="pt-10 flex items-center gap-6">
                      <div className="w-12 h-[2px] bg-orange-500"></div>
                      <p className="text-[10px] font-black uppercase tracking-[0.4em] text-slate-300">End of story</p>
                    </div>
                  </div>
                </div>

                {/* Fixed Footer inside Modal */}
                <div className="px-8 md:px-16 py-6 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
                  <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Published on Sunshine Travels</p>
                  <button onClick={() => setSelectedTestimonial(null)} className="text-slate-900 font-black uppercase tracking-widest text-[9px] hover:text-orange-500 transition-colors">Close</button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Compact Sophisticated Hero */}
      <section className="relative min-h-[60vh] flex items-center pt-32 pb-16">
        <div className="absolute inset-0 z-0">
          <img
            src={testimonialsBg}
            alt="Serene Tropical Sunset"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-slate-900/10"></div>
          {/* Bottom Fade to match background */}
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#FAF9F6] to-transparent"></div>
        </div>

        <div className="container mx-auto px-6 relative z-10 text-center space-y-6">
          <motion.span
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="inline-block px-4 py-1.5 rounded-full bg-orange-500 text-white text-[12px] font-bold uppercase tracking-wider"
          >
            Reviews
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-5xl md:text-7xl font-bold text-white tracking-wide drop-shadow-2xl"
          >
            Customer <span className="text-orange-500 italic">Stories.</span>
          </motion.h1>
          <div className="w-16 h-1 bg-orange-500 mx-auto"></div>

          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            onClick={() => {
              if (!user) {
                setSubmitStatus('login_required');
                setTimeout(() => setSubmitStatus(null), 3000);
                return;
              }
              setShowForm(!showForm);
            }}
            className="mt-8 bg-slate-900 text-white px-8 py-3 rounded-full font-bold text-sm tracking-widest uppercase hover:bg-orange-500 transition-all shadow-lg"
          >
            {showForm ? "Close Form" : (user ? "Share Your Story" : "Login to Share Story")}
          </motion.button>
        </div>
      </section>

      {/* Google Reviews Trust Widget */}
      <section className="py-12 bg-[#FAF9F6] relative z-20 -mt-10">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto bg-white rounded-[2.5rem] p-6 md:p-10 flex flex-col md:flex-row items-center justify-between gap-10 shadow-[0_20px_50px_rgba(0,0,0,0.1)] border border-slate-100"
          >
            <div className="flex items-center gap-8">
              <div className="w-20 h-20 bg-slate-50 rounded-3xl flex items-center justify-center shadow-inner border border-slate-100">
                <svg viewBox="0 0 24 24" className="w-12 h-12">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-1.5">
                  {[...Array(5)].map((_, i) => <Star key={i} size={18} className="fill-orange-400 text-orange-400" />)}
                  <span className="ml-3 text-xl font-black text-slate-900 tracking-tight">4.9 / 5.0</span>
                </div>
                <p className="text-slate-500 font-bold uppercase tracking-widest text-[10px]">
                  Based on genuine Google Business Reviews
                </p>
              </div>
            </div>

            <a
              href="https://www.google.com/maps/place/Sunshine+Travels+and+Holidays/@13.3552035,74.7619176,13z/data=!4m8!3m7!1s0x3bbca51cac348ccd:0x1f114dc0c7a1352c!8m2!3d13.351605!4d74.7875424!9m1!1b1!16s%2Fg%2F11gnsd3jpb?entry=ttu&g_ep=EgoyMDI2MDUxMi4wIKXMDSoASAFQAw%3D%3D"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#4285F4] hover:bg-[#3367D6] text-white px-10 py-5 rounded-[1.5rem] font-bold transition-all shadow-[0_15px_30px_rgba(66,133,244,0.3)] flex items-center gap-4 group uppercase tracking-widest text-[11px]"
            >
              <Star size={20} className="fill-white group-hover:rotate-[30deg] transition-transform duration-500" />
              View All Google Reviews
            </a>
          </motion.div>
        </div>
      </section>

      {/* Feedback Form Section */}
      <AnimatePresence>
        {showForm && (
          <motion.section
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="container mx-auto px-6 overflow-hidden pb-12"
          >
            <div className="max-w-2xl mx-auto bg-white p-8 md:p-12 rounded-[2rem] shadow-xl border border-slate-100">
              <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center tracking-wide">We value your <span className="text-orange-500">feedback.</span></h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 ml-4">Full Name</label>
                    <input
                      required
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="e.g. John Doe"
                      className="w-full px-6 py-4 rounded-2xl bg-slate-50 border-none focus:ring-2 focus:ring-orange-500 transition-all outline-none text-slate-900"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 ml-4">Your Role</label>
                    <input
                      type="text"
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      placeholder="e.g. Solo Traveler"
                      className="w-full px-6 py-4 rounded-2xl bg-slate-50 border-none focus:ring-2 focus:ring-orange-500 transition-all outline-none text-slate-900"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 ml-4">Your Rating</label>
                  <div className="flex gap-3 ml-4">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, rating: star }))}
                        className="transition-all hover:scale-110 active:scale-95"
                      >
                        <Star
                          size={28}
                          className={star <= formData.rating ? "fill-orange-500 text-orange-500" : "text-slate-200"}
                        />
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between items-center ml-4">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Your Story</label>
                    <span className={`text-[10px] font-bold uppercase tracking-widest ${wordCount >= 200 ? 'text-red-500' : 'text-slate-400'}`}>
                      {wordCount}/200 words
                    </span>
                  </div>
                  <textarea
                    required
                    name="content"
                    value={formData.content}
                    onChange={handleInputChange}
                    placeholder="Tell us about your experience..."
                    rows="4"
                    className="w-full px-6 py-4 rounded-2xl bg-slate-50 border-none focus:ring-2 focus:ring-orange-500 transition-all outline-none text-slate-900 resize-none"
                  ></textarea>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 ml-4">Profile Picture</label>
                  <div className="flex items-center gap-4 bg-slate-50 p-4 rounded-2xl border-2 border-dashed border-slate-200 hover:border-orange-400 transition-all group relative overflow-hidden">
                    {formData.avatar_url ? (
                      <div className="relative w-16 h-16 rounded-xl overflow-hidden shadow-md">
                        <img src={formData.avatar_url} alt="Preview" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, avatar_url: '' }))}
                          className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-[10px] font-bold"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div className="w-16 h-16 rounded-xl bg-slate-200 flex items-center justify-center text-slate-400">
                        <ImageIcon size={24} />
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="text-xs font-semibold text-slate-600">
                        {formData.avatar_url ? "Photo selected" : "Upload your photo"}
                      </p>
                      <p className="text-[10px] text-slate-400 mt-1">PNG, JPG up to 2MB</p>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            setFormData(prev => ({ ...prev, avatar_url: reader.result }));
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                      className="absolute inset-0 opacity-0 cursor-pointer"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-5 bg-orange-500 text-white rounded-2xl font-bold text-sm uppercase tracking-[0.2em] hover:bg-slate-900 transition-all shadow-lg flex items-center justify-center gap-3 disabled:opacity-50"
                >
                  {isSubmitting ? "Submitting..." : "Post Review"}
                  <ArrowRight size={18} />
                </button>
              </form>
            </div>
          </motion.section>
        )}
      </AnimatePresence>

      <section className="py-20 container mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <AnimatePresence>
            {isLoading ? (
              [...Array(6)].map((_, i) => (
                <motion.div
                  key={`skeleton-${i}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <TestimonialSkeleton />
                </motion.div>
              ))
            ) : (
              testimonials.map((t, index) => (
                <TestimonialCard key={t.id} testimonial={t} index={index} onClick={setSelectedTestimonial} />
              ))
            )}
          </AnimatePresence>
        </div>
      </section>

      <AnimatePresence>
        {submitStatus && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className={`fixed top-24 right-8 px-8 py-4 rounded-2xl shadow-2xl z-50 font-bold ${submitStatus === 'success' ? 'bg-orange-500 text-white' :
              submitStatus === 'login_required' ? 'bg-slate-900 text-white border border-orange-500' : 'bg-red-500 text-white'
              }`}
          >
            {submitStatus === 'success' ? 'Feedback pending, until admin approves' :
              submitStatus === 'login_required' ? 'Please login to share your story' : 'Failed to submit. Please try again.'}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Brand Trust Section - Compact */}
      <section className="pb-32 px-6">
        <div className="max-w-6xl mx-auto bg-slate-900 rounded-[4rem] overflow-hidden shadow-2xl flex flex-col md:flex-row items-stretch group">
          {/* Left Side: Kathakali Image Frame */}
          <div className="md:w-1/2 relative min-h-[400px] md:min-h-[500px] overflow-hidden">
            <img
              src={kathakaliBg}
              alt="Kathakali Dancer"
              className="w-full h-full object-cover transition-transform duration-[3s] group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-slate-900/40"></div>
          </div>

          {/* Right Side: CTA Content */}
          <div className="md:w-1/2 p-12 md:p-20 flex flex-col justify-center space-y-8 bg-slate-900/50 backdrop-blur-sm relative">
            <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/5 rounded-full blur-3xl -mr-32 -mt-32"></div>

            <div className="space-y-4 relative z-10">
              <span className="text-orange-500 font-black uppercase tracking-[0.4em] text-[10px] block">Start Your Story</span>
              <h2 className="text-4xl md:text-6xl font-serif font-bold text-white tracking-wide leading-tight">
                Ready for your <br />
                <span className="text-orange-500 italic">next trip?</span>
              </h2>
              <p className="text-white/70 font-medium leading-relaxed text-lg max-w-md">
                Join thousands of happy travelers who have seen the world with Sunshine Travels.
              </p>
            </div>

            <div className="pt-4 relative z-10">
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-orange-500 text-white px-10 py-5 rounded-full font-black shadow-xl flex items-center gap-3 hover:bg-white hover:text-slate-900 transition-all uppercase tracking-widest text-xs"
                >
                  Contact Us <ArrowRight size={20} />
                </motion.button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Subtle Brand Strip */}
      <section className="pb-24 container mx-auto px-6">
        <div className="flex flex-wrap justify-center items-center gap-12 md:gap-20 opacity-20 grayscale">
          {['LUXURY ESCAPES', 'TRAVEL INSIDER', 'THE COLLECTIVE', 'VOGUE TRAVEL'].map((brand) => (
            <div key={brand} className="text-sm font-bold tracking-widest text-slate-900">{brand}</div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Testimonials;
